'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { BringtableLogo } from '@/components/branding/BringtableLogo';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { LangSwitcher } from './LangSwitcher';

interface LandingNavProps {
  isAuthenticated: boolean;
  overlay?: boolean;
}

export function LandingNav({ isAuthenticated, overlay = false }: LandingNavProps) {
  const t = useTranslations('landing.nav');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Theme detection based on scroll position
      const sections = document.querySelectorAll('section[data-theme]');
      let currentTheme: 'light' | 'dark' = 'dark';

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          const sectionTheme = section.getAttribute('data-theme');
          if (sectionTheme === 'light' || sectionTheme === 'dark') {
            currentTheme = sectionTheme;
          }
        }
      });
      setTheme(currentTheme);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu with Escape
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const isLight = theme === 'light';
  const textClass = isLight ? 'text-black' : 'text-white';
  const textHoverClass = isLight ? 'hover:text-black/70' : 'hover:text-white/70';

  return (
    <nav
      className={cn(
        'fixed inset-x-0 top-0 z-[100] w-full transition-all duration-500',
        scrolled ? 'py-2' : 'py-3',
        scrolled && (isLight ? 'border-b border-black/10' : 'border-b border-white/10'),
      )}
    >
      {/* Masked background layer */}
      <div
        className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
        style={
          !scrolled
            ? {
                maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
              }
            : undefined
        }
      >
        <div
          className={cn(
            'absolute inset-0 transition-all duration-500',
            scrolled ? 'backdrop-blur-[20px]' : 'backdrop-blur-[12px]',
            scrolled
              ? isLight
                ? 'bg-white/85'
                : 'bg-black/75'
              : isLight
                ? 'bg-white/20'
                : 'bg-black/10',
          )}
        />
      </div>

      <div className="container mx-auto px-6 md:px-8 flex items-center justify-between relative z-10">
        {/* Logo (Left) */}
        <div className="flex-1">
          <BringtableLogo
            className={cn(
              'inline-flex items-center gap-2 transition-colors duration-500',
              textClass,
            )}
          />
        </div>

        {/* Desktop Navigation (Center) */}
        <div className="hidden md:flex items-center justify-center gap-8 flex-1">
          <Link
            href="/#features"
            className={cn(
              'text-sm font-medium transition-colors duration-500',
              textClass,
              textHoverClass,
            )}
          >
            {t('features')}
          </Link>
          <Link
            href="/#four-steps"
            className={cn(
              'text-sm font-medium transition-colors duration-500',
              textClass,
              textHoverClass,
            )}
          >
            {t('howItWorks')}
          </Link>
          <Link
            href="/#pricing"
            className={cn(
              'text-sm font-medium transition-colors duration-500',
              textClass,
              textHoverClass,
            )}
          >
            {t('pricing')}
          </Link>
          <Link
            href="/#case-studies"
            className={cn(
              'text-sm font-medium transition-colors duration-500',
              textClass,
              textHoverClass,
            )}
          >
            {t('caseStudies')}
          </Link>
        </div>

        {/* CTA Buttons (Right) */}
        <div className="hidden md:flex items-center justify-end gap-5 flex-1">
          <LangSwitcher textClass={textClass} />
          {isAuthenticated ? (
            <Link
              href="/org/select"
              className="group relative inline-flex h-9 items-center justify-center overflow-hidden rounded-full bg-primary px-4 py-2 font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,108,92,0.4)]"
            >
              <span className="relative flex items-center gap-1 text-sm">
                {t('goToDashboard')}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ) : (
            <>
              <Link
                href="/login"
                className={cn(
                  'text-sm font-medium transition-colors duration-500',
                  textClass,
                  textHoverClass,
                )}
              >
                {t('logIn')}
              </Link>
              <Link
                href="/pricing"
                className="group relative inline-flex h-9 items-center justify-center overflow-hidden rounded-full bg-primary px-4 py-2 font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,108,92,0.4)]"
              >
                <span className="relative flex items-center gap-1 text-sm">
                  {t('tryForFree')}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className={cn(
            'md:hidden p-2 rounded-lg transition-colors duration-500 focus-visible:outline-none focus-visible:ring-2',
            isLight
              ? 'text-black/80 focus-visible:ring-black/30'
              : 'text-white/80 focus-visible:ring-white/40',
          )}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('toggleMenu')}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-nav-menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          id="mobile-nav-menu"
          className={cn(
            'absolute top-full left-0 w-full border-b px-6 py-6 space-y-6 backdrop-blur-xl md:hidden transition-colors duration-500',
            isLight ? 'bg-white/95 border-black/10' : 'bg-black/95 border-white/10',
          )}
        >
          <Link
            href="/#features"
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              'block text-lg font-medium transition-colors',
              isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white',
            )}
          >
            {t('features')}
          </Link>
          <Link
            href="/#four-steps"
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              'block text-lg font-medium transition-colors',
              isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white',
            )}
          >
            {t('howItWorks')}
          </Link>
          <Link
            href="/#pricing"
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              'block text-lg font-medium transition-colors',
              isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white',
            )}
          >
            {t('pricing')}
          </Link>
          <Link
            href="/#case-studies"
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              'block text-lg font-medium transition-colors',
              isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white',
            )}
          >
            {t('caseStudies')}
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitcher
              textClass={
                isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white'
              }
            />
          </div>
          <div
            className={cn(
              'pt-4 flex flex-col gap-4 border-t',
              isLight ? 'border-black/10' : 'border-white/10',
            )}
          >
            {isAuthenticated ? (
              <Link
                href="/org/select"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-base font-semibold text-white"
              >
                {t('goToDashboard')}
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'text-lg font-medium transition-colors',
                    isLight ? 'text-black/80 hover:text-black' : 'text-white/80 hover:text-white',
                  )}
                >
                  {t('logIn')}
                </Link>
                <Link
                  href="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-base font-semibold text-white"
                >
                  {t('tryForFree')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
